import type {
  BinogQuestion,
  BinogQuestionOption,
  LocalizedText,
  QuestionLevel,
  QuestionSource,
} from "@binogi-livequiz/shared";
import { api } from "./api";
import { decodeQuestionsParam } from "./decode-questions";

// Canonical Binogi lesson API. Returns one JSON document containing the
// lesson title, every question, and per-language translations for every
// question + option text in a single payload. The `countryCode=XX` form
// asks for the global cut (no market-specific filtering).
const API_BASE = "https://lessons.binogi.net/api/v3/lessons";
const COUNTRY_CODE = "XX";

export interface FetchedLesson {
  lessonCode: string;
  title: LocalizedText;
  questions: BinogQuestion[];
  languages: string[]; // langs the lesson is renderable in end-to-end
}

/** The longest lesson code the host input takes. */
export const LESSON_CODE_MAX_LENGTH = 20;
const VALID_CODE = new RegExp(`^[A-Z0-9_-]{3,${LESSON_CODE_MAX_LENGTH}}$`);

/**
 * A lesson load failure the teacher can act on. `i18nKey` names the copy
 * HostReview shows, with the lesson code as `{{code}}`; the English
 * `message` is for logs only.
 */
export class LessonLoadError extends Error {
  constructor(
    readonly i18nKey: string,
    readonly lessonCode: string,
    message: string,
  ) {
    super(message);
    this.name = "LessonLoadError";
  }
}

export function isValidLessonCode(code: string): boolean {
  return VALID_CODE.test(code.trim().toUpperCase());
}

// Single-document multilingual fetch. The API returns every language
// variant in one payload; no need for parallel-per-language fetches.
// `languages` in the returned shape is the intersection across questions
// and options, so a student's language picker can be filtered to only
// the languages the whole quiz is renderable in.
export async function fetchLessonAllLanguages(
  rawCode: string,
): Promise<FetchedLesson> {
  const code = rawCode.trim().toUpperCase();
  if (!isValidLessonCode(code)) {
    throw new LessonLoadError(
      "host.input.error_invalid_code",
      code,
      "Lesson codes are short alphanumeric IDs like BIH750 or GEH101.",
    );
  }
  const url = `${API_BASE}/${code}?countryCode=${COUNTRY_CODE}`;
  let res: Response;
  try {
    res = await fetch(url);
  } catch (err) {
    throw new LessonLoadError(
      "host.input.error_unreachable",
      code,
      `Could not reach the lesson server. ${
        err instanceof Error ? err.message : "Unknown network error."
      }`,
    );
  }
  if (res.status === 404 || res.status === 403) {
    throw new LessonLoadError(
      "host.input.error_not_found",
      code,
      `No lesson found for code "${code}".`,
    );
  }
  if (!res.ok) {
    throw new Error(`Failed to load lesson "${code}" (HTTP ${res.status}).`);
  }
  let data: unknown;
  try {
    data = await res.json();
  } catch {
    throw new Error(`Lesson "${code}" returned malformed JSON.`);
  }

  const parsed = parseLesson(code, data);
  if (parsed.questions.length === 0) {
    throw new LessonLoadError(
      "host.input.error_no_questions",
      code,
      `Lesson "${code}" has no single-choice questions.`,
    );
  }
  return parsed;
}

// Legacy alias retained so any out-of-tree caller still resolves; current
// in-repo callers all use fetchLessonAllLanguages.
export const fetchLesson = fetchLessonAllLanguages;

function parseLesson(code: string, data: unknown): FetchedLesson {
  if (!data || typeof data !== "object") {
    throw new Error(`Lesson "${code}" JSON is malformed.`);
  }
  const obj = data as Record<string, unknown>;

  const lessonCode =
    typeof obj["code"] === "string" ? (obj["code"] as string) : code;

  // Return the full {lang: title} map so the teacher's picker can show
  // the title in the language they're previewing. Falls back to the
  // lesson code if no titles are present at all.
  const title = normalizeLocalized(obj["titles"]) ?? lessonCode;

  const quizObj = obj["quiz"];
  const rawQuestions =
    quizObj && typeof quizObj === "object" && Array.isArray((quizObj as Record<string, unknown>)["questions"])
      ? ((quizObj as Record<string, unknown>)["questions"] as unknown[])
      : Array.isArray(obj["questions"])
        ? (obj["questions"] as unknown[])
        : [];

  // singleChoice only for now — multiChoice handling is reserved for a
  // later milestone where the student-side UI gets a confirm step.
  const questions = rawQuestions
    .map(normalizeQuestion)
    .filter((q): q is BinogQuestion => q !== null && q.type === "singleChoice");

  const languages = intersectLanguages(questions);
  return { lessonCode, title, questions, languages };
}

function normalizeQuestion(raw: unknown): BinogQuestion | null {
  if (!raw || typeof raw !== "object") return null;
  const q = raw as Record<string, unknown>;
  const qid = typeof q["qid"] === "string" ? (q["qid"] as string) : null;
  const text = normalizeLocalized(q["text"]);
  if (!qid || !text) return null;

  const type: BinogQuestion["type"] =
    q["type"] === "multiChoice" ? "multiChoice" : "singleChoice";
  const level: QuestionLevel =
    q["level"] === 2 ? 2 : q["level"] === 3 ? 3 : 1;
  const pictureUrl =
    typeof q["pictureUrl"] === "string" ? (q["pictureUrl"] as string) : null;

  const rawOptions = Array.isArray(q["options"]) ? (q["options"] as unknown[]) : [];
  const options: BinogQuestionOption[] = rawOptions
    .map((opt) => {
      if (!opt || typeof opt !== "object") return null;
      const o = opt as Record<string, unknown>;
      const oid = typeof o["oid"] === "string" ? (o["oid"] as string) : null;
      const oText = normalizeLocalized(o["text"]);
      if (!oid || !oText) return null;
      return { oid, text: oText, isCorrect: Boolean(o["isCorrect"]) };
    })
    .filter((o): o is BinogQuestionOption => o !== null);

  if (options.length < 2) return null;
  const correctCount = options.filter((o) => o.isCorrect).length;
  if (correctCount === 0) return null;
  if (type === "singleChoice" && correctCount !== 1) {
    let kept = false;
    for (const o of options) {
      if (o.isCorrect && !kept) {
        kept = true;
      } else if (o.isCorrect) {
        o.isCorrect = false;
      }
    }
  }

  return { qid, type, level, text, pictureUrl, options };
}

// The API wraps every localized field in a country-code outer layer:
//   { XX: { en: "...", sv: "...", ... } }
// Country `XX` is the global cut; if it's absent, fall back to the first
// country block present.
function pickByCountry(
  raw: unknown,
  preferred: string,
): Record<string, unknown> | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const got = r[preferred];
  if (got && typeof got === "object") return got as Record<string, unknown>;
  for (const k of Object.keys(r)) {
    const v = r[k];
    if (v && typeof v === "object") return v as Record<string, unknown>;
  }
  return null;
}

function normalizeLocalized(raw: unknown): LocalizedText | null {
  const byLang = pickByCountry(raw, COUNTRY_CODE);
  if (!byLang) {
    // Tolerate a plain string (older sources, inline payloads).
    if (typeof raw === "string" && raw.length > 0) return raw;
    return null;
  }
  const out: { [lang: string]: string } = {};
  for (const k of Object.keys(byLang)) {
    const v = byLang[k];
    if (typeof v === "string" && v.length > 0) out[k] = v;
  }
  if (Object.keys(out).length === 0) return null;
  return Object.keys(out).length === 1 ? out[Object.keys(out)[0]!]! : out;
}

function langsOf(t: LocalizedText): Set<string> {
  if (typeof t === "string") return new Set(["en"]);
  return new Set(Object.keys(t));
}

function intersectLanguages(questions: BinogQuestion[]): string[] {
  if (questions.length === 0) return ["en"];
  let common: Set<string> | null = null;
  for (const q of questions) {
    const here = langsOf(q.text);
    for (const o of q.options) {
      const oHere = langsOf(o.text);
      for (const k of [...here]) if (!oHere.has(k)) here.delete(k);
    }
    if (common === null) {
      common = here;
    } else {
      for (const k of [...common]) if (!here.has(k)) common.delete(k);
    }
  }
  const result = common ? [...common] : ["en"];
  return result.length > 0 ? result : ["en"];
}

// Filter questions by level. Accepts a list of QuestionLevel values.
export function filterByLevels(
  questions: BinogQuestion[],
  levels: QuestionLevel[],
): BinogQuestion[] {
  if (levels.length === 0) return questions;
  const allowed = new Set(levels);
  return questions.filter((q) => allowed.has(q.level));
}

// Parse a ?level= URL param. Accepts "1", "1,2", "2,3". Returns [] if absent
// or unparseable so callers can treat it as no-filter.
export function parseLevelParam(raw: string | null): QuestionLevel[] {
  if (!raw) return [];
  const out: QuestionLevel[] = [];
  for (const part of raw.split(",")) {
    const n = Number.parseInt(part.trim(), 10);
    if (n === 1 || n === 2 || n === 3) out.push(n as QuestionLevel);
  }
  return out;
}

/** A question set ready for the host to curate, from any source. */
export interface LoadedLesson {
  lessonCode: string;
  // {lang: title} from lessons.binogi.net, or a plain string for inline /
  // staged sources. Rendered via pickLocalized(title, currentLang).
  title: LocalizedText;
  questions: BinogQuestion[];
  // Languages the loaded questions are fully renderable in. For inline /
  // staged sources this is ["en"] (single language); for lesson-code loads
  // this is the intersection of langs each question has translations for.
  languages: string[];
  // Decides the initial selection: a lesson-code load preselects the
  // shortest few, a staged or inline set starts fully selected.
  source: QuestionSource;
}

/** Where the host's questions come from, with an optional level filter. */
export type LessonRequest =
  | { source: "inline"; questions: string; levels: QuestionLevel[] }
  | { source: "staged"; stageId: string; levels: QuestionLevel[] }
  | { source: "lesson"; code: string; levels: QuestionLevel[] };

// The title inline and staged sets carry; the host screen swaps it for a
// localized placeholder, as it does for these lesson codes.
export const PLACEHOLDER_TITLE = "Questions from Binogi";
const PLACEHOLDER_LESSON_CODES = new Set(["INLINE", "STAGED"]);

export function isPlaceholderLessonCode(code: string): boolean {
  return PLACEHOLDER_LESSON_CODES.has(code);
}

/**
 * The hand-off in the /host URL: `?questions=` (inline), `?stageId=`
 * (staged), or `?lesson=` / `?code=` (lesson code), in that order of
 * precedence, each with an optional `?level=` filter. Null when none is set.
 */
export function lessonRequestFromParams(
  params: URLSearchParams,
): LessonRequest | null {
  const questions = params.get("questions");
  const stageId = params.get("stageId");
  const code = params.get("lesson") ?? params.get("code");
  const levels = parseLevelParam(params.get("level"));
  if (questions) return { source: "inline", questions, levels };
  if (stageId) return { source: "staged", stageId, levels };
  if (code) return { source: "lesson", code, levels };
  return null;
}

/**
 * Load the questions a request names. A lesson code whose level filter
 * leaves nothing throws a `LessonLoadError`, like any other lesson load
 * failure the teacher can act on.
 */
export async function loadLesson(request: LessonRequest): Promise<LoadedLesson> {
  switch (request.source) {
    case "inline":
      return {
        lessonCode: "INLINE",
        title: PLACEHOLDER_TITLE,
        questions: filterByLevels(
          decodeQuestionsParam(request.questions),
          request.levels,
        ),
        languages: ["en"],
        source: "inline",
      };
    case "staged": {
      const stage = await api.getStage(request.stageId);
      return {
        lessonCode: "STAGED",
        title: PLACEHOLDER_TITLE,
        questions: filterByLevels(stage.questions, request.levels),
        languages: ["en"],
        source: "staged",
      };
    }
    case "lesson": {
      const { code, levels } = request;
      const lesson = await fetchLessonAllLanguages(code);
      const filtered = filterByLevels(lesson.questions, levels);
      if (filtered.length === 0 && levels.length > 0) {
        throw new LessonLoadError(
          "host.input.error_no_level_questions",
          code.toUpperCase(),
          `Lesson "${code.toUpperCase()}" has no level-${levels.join(",")} questions.`,
        );
      }
      return {
        lessonCode: lesson.lessonCode,
        title: lesson.title,
        questions: filtered,
        languages: lesson.languages,
        source: "lesson",
      };
    }
  }
}
